import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { HeartPulse } from 'lucide-react';

/* Google Health connection card.
   - Test users (allow-listed in Google Cloud "Testing" mode) can connect now.
   - Everyone else can tap the waitlist so we email them when it's public. */
const GoogleHealthCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/health/status`);
      setStatus(res.data);
    } catch (e) {
      setStatus({ connected: false });
    }
    try {
      const w = await axios.get(`${API}/interest/google_health`);
      setWaitlisted(!!w.data?.registered);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const connect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.get(`${API}/health/google/connect`);
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      setBusy(false);
    }
  };

  const sync = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/health/sync`);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not sync Google Health right now.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/health/disconnect`);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const joinWaitlist = async () => {
    if (busy || waitlisted) return;
    setBusy(true);
    try {
      await axios.post(`${API}/interest/google_health`);
      setWaitlisted(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 mb-4" data-testid="google-health-card">
      <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <HeartPulse size={20} className="text-emerald-600" /> Google Fit Sync
      </h3>

      {status?.connected ? (
        <>
          <p className="text-sm text-emerald-600 font-medium mb-3" data-testid="google-health-connected">
            ✅ Connected{status.last_steps != null ? ` · ${status.last_steps.toLocaleString()} steps last sync` : ''}
          </p>
          <div className="flex gap-3">
            <button
              data-testid="google-health-sync-button"
              onClick={sync}
              disabled={busy}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition disabled:opacity-50"
            >
              Sync now
            </button>
            <button
              data-testid="google-health-disconnect-button"
              onClick={disconnect}
              disabled={busy}
              className="py-3 px-4 border border-gray-200 text-gray-500 font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-gray-500 text-sm mb-4">
            Auto-import your steps and activity from Google Fit. Available now for testers —
            tap connect, or join the waitlist and we&apos;ll email you when it opens to everyone.
          </p>
          <button
            data-testid="google-health-connect-button"
            onClick={connect}
            disabled={busy}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <HeartPulse size={16} /> Connect Google Fit
          </button>
          {waitlisted ? (
            <p className="text-xs text-emerald-600 font-medium mt-3 text-center" data-testid="google-health-waitlisted">
              ✅ You&apos;re on the waitlist — we&apos;ll email you when it&apos;s public.
            </p>
          ) : (
            <button
              data-testid="google-health-waitlist-button"
              onClick={joinWaitlist}
              disabled={busy}
              className="w-full text-xs text-gray-400 hover:text-emerald-600 mt-3 transition"
            >
              Not a tester yet? Join the waitlist
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default GoogleHealthCard;
